import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PresentationMode from './PresentationMode.jsx';
import useApi from '../hooks/useApi';
import { PROGRAMS } from '../constants/programs';
import { useNavigate } from 'react-router-dom';

const CENTRAL_TIMEZONE = 'America/Chicago';
const ALL_PROGRAMS = 'All Programs';
const PREFERENCES_KEY = 'dailyDigestPrefs';
const DEFAULT_SECTION_STATE = {
  triggerExecutions: true,
  dpeStatusChanges: true,
  appsheetStatusChanges: false,
  actionRequiredChanges: false,
  vmtPushes: false,
  autoStatusUpdates: true,
  dbErrorLogs: true,
};

const formatDateTime = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString('en-US', {
    timeZone: CENTRAL_TIMEZONE,
    dateStyle: 'medium',
    timeStyle: 'short',
  });
};

const formatDayLabel = (dayKey) => {
  if (!dayKey) return '';
  const parts = String(dayKey).split('-').map((p) => Number(p));
  if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return dayKey;
  const [year, month, day] = parts;
  const date = new Date(year, month - 1, day);
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
  });
};

const loadPrefs = () => {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(PREFERENCES_KEY);
    if (!raw) return {};
    return JSON.parse(raw) || {};
  } catch (_err) {
    return {};
  }
};

const savePrefs = (prefs) => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(PREFERENCES_KEY, JSON.stringify(prefs));
  } catch (_err) {
    // noop
  }
};

const normalizeSearch = (value) => String(value || '').trim().toLowerCase();

const rowMatchesSearch = (row, search) => {
  if (!search) return true;
  return Object.values(row || {}).some((value) => {
    if (value === null || value === undefined) return false;
    if (Array.isArray(value)) {
      return value.some((entry) => String(entry || '').toLowerCase().includes(search));
    }
    return String(value).toLowerCase().includes(search);
  });
};

const applyFilters = (rows, filters) => {
  const list = Array.isArray(rows) ? rows : [];
  const programName = filters.programName && filters.programName !== ALL_PROGRAMS ? filters.programName : null;
  const bac = String(filters.bac || '').trim();
  const search = normalizeSearch(filters.search);

  return list.filter((row) => {
    if (programName) {
      const rowProgram = row.programName || '';
      const programNames = Array.isArray(row.programNames) ? row.programNames : [];
      if (rowProgram !== programName && !programNames.includes(programName)) return false;
    }
    if (bac) {
      const rowBac = String(row.bac || '').trim();
      if (rowBac !== bac) return false;
    }
    if (search && !rowMatchesSearch(row, search)) return false;
    return true;
  });
};

const computeStatusCounts = (rows) => {
  const counts = { COOLING: 0, CANCELLED: 0, OPTED_OUT: 0, TERMINATED: 0 };
  rows.forEach((row) => {
    const normalized = String(row.newStatus || '')
      .trim()
      .replace(/\s+/g, '_')
      .toUpperCase();
    const next = normalized === 'CANCELED' ? 'CANCELLED' : normalized;
    if (counts[next] !== undefined) {
      counts[next] += 1;
    }
  });
  return counts;
};

const toCsvValue = (value) => {
  if (value === null || value === undefined) return '';
  const raw = Array.isArray(value) ? value.join(', ') : String(value);
  if (raw.includes('"') || raw.includes(',') || raw.includes('\n')) {
    return `"${raw.replace(/"/g, '""')}"`;
  }
  return raw;
};

const exportCsv = ({ rows, columns, filename }) => {
  const headers = columns.map((col) => col.headerName || col.field || col.id);
  const fields = columns.map((col) => col.field || col.id);
  const lines = [headers.join(',')];
  rows.forEach((row) => {
    const line = fields.map((field) => toCsvValue(row?.[field]));
    lines.push(line.join(','));
  });
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
};

const exportJson = ({ rows, filename }) => {
  const blob = new Blob([JSON.stringify(rows, null, 2)], { type: 'application/json;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
};

const ExportMenu = ({ rows, columns, filename }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  return (
    <>
      <Button
        size="small"
        variant="outlined"
        startIcon={<FileDownloadIcon />}
        onClick={(event) => setAnchorEl(event.currentTarget)}
      >
        Export
      </Button>
      <Menu anchorEl={anchorEl} open={open} onClose={() => setAnchorEl(null)}>
        <MenuItem
          onClick={() => {
            exportCsv({ rows, columns, filename: `${filename}.csv` });
            setAnchorEl(null);
          }}
        >
          CSV
        </MenuItem>
        <MenuItem
          onClick={() => {
            exportJson({ rows, filename: `${filename}.json` });
            setAnchorEl(null);
          }}
        >
          JSON
        </MenuItem>
      </Menu>
    </>
  );
};

const DigestTable = ({ rows, columns }) => {
  const safeRows = Array.isArray(rows) ? rows : [];
  return (
    <DataGrid
      autoHeight
      rows={safeRows}
      columns={columns}
      disableRowSelectionOnClick
      pageSizeOptions={[5, 10, 25]}
      initialState={{ pagination: { paginationModel: { pageSize: 5 } } }}
      density="compact"
    />
  );
};

const SectionHeader = ({ title, count }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
      {title}
    </Typography>
    <Chip size="small" label={count} color={count > 0 ? 'primary' : 'default'} variant="outlined" />
  </Box>
);

const DataStory = () => {
  const { useDailyDigest } = useApi();
  const navigate = useNavigate();
  const { data, isLoading, error } = useDailyDigest({ days: 7 });
  const days = useMemo(() => (Array.isArray(data?.days) ? data.days : []), [data?.days]);
  const sources = data?.sources || null;
  const [activeDayKey, setActiveDayKey] = useState(null);
  const [filters, setFilters] = useState({ programName: ALL_PROGRAMS, bac: '', search: '' });
  const [prefs, setPrefs] = useState(() => loadPrefs());
  const hasHydratedDayRef = useRef(false);

  useEffect(() => {
    if (!days.length || hasHydratedDayRef.current) return;
    hasHydratedDayRef.current = true;
    const lastDayKey = prefs?.lastDayKey;
    const preferredDay = lastDayKey ? days.find((day) => day.dayKey === lastDayKey) : null;
    setActiveDayKey(preferredDay?.dayKey || days[0]?.dayKey || null);
  }, [days, prefs?.lastDayKey]);

  useEffect(() => {
    if (!days.length || !hasHydratedDayRef.current) return;
    if (!activeDayKey) {
      setActiveDayKey(days[0]?.dayKey || null);
      return;
    }
    const exists = days.some((day) => day.dayKey === activeDayKey);
    if (!exists) setActiveDayKey(days[0]?.dayKey || null);
  }, [activeDayKey, days]);

  const activeIndex = useMemo(() => {
    if (!days.length) return -1;
    if (!activeDayKey) return 0;
    return days.findIndex((day) => day.dayKey === activeDayKey);
  }, [activeDayKey, days]);

  const normalizedActiveIndex = activeIndex >= 0 ? activeIndex : 0;

  useEffect(() => {
    const activeDay = days[normalizedActiveIndex];
    if (!activeDay) return;
    setPrefs((prev) => {
      if (!prev) {
        const nextPrefs = { lastDayKey: activeDay.dayKey };
        savePrefs(nextPrefs);
        return nextPrefs;
      }
      if (prev.lastDayKey === activeDay.dayKey) return prev;
      const nextPrefs = { ...prev, lastDayKey: activeDay.dayKey };
      savePrefs(nextPrefs);
      return nextPrefs;
    });
  }, [days, normalizedActiveIndex]);

  const triggerColumns = useMemo(
    () => [
      {
        field: 'executedAt',
        headerName: 'Executed',
        minWidth: 170,
        flex: 1,
        renderCell: (params) => formatDateTime(params?.row?.executedAt ?? params?.value),
      },
      { field: 'programName', headerName: 'Program', minWidth: 140, flex: 1 },
      { field: 'templateName', headerName: 'Template', minWidth: 160, flex: 1 },
      { field: 'scope', headerName: 'Scope', minWidth: 140, flex: 1 },
      { field: 'status', headerName: 'Status', minWidth: 120, flex: 1 },
    ],
    []
  );

  const dpeColumns = useMemo(
    () => [
      {
        field: 'changedAt',
        headerName: 'Changed',
        minWidth: 170,
        flex: 1,
        renderCell: (params) => formatDateTime(params?.row?.changedAt ?? params?.value),
      },
      { field: 'programName', headerName: 'Program', minWidth: 140, flex: 1 },
      { field: 'bac', headerName: 'BAC', minWidth: 110, flex: 1 },
      { field: 'dealerName', headerName: 'Dealer', minWidth: 180, flex: 1.2 },
      { field: 'previousStatus', headerName: 'Previous', minWidth: 140, flex: 1 },
      { field: 'newStatus', headerName: 'New', minWidth: 140, flex: 1 },
    ],
    []
  );

  const appsheetColumns = useMemo(
    () => [
      {
        field: 'changedAt',
        headerName: 'Changed',
        minWidth: 170,
        flex: 1,
        renderCell: (params) => formatDateTime(params?.row?.changedAt ?? params?.value),
      },
      { field: 'programName', headerName: 'Program', minWidth: 140, flex: 1 },
      { field: 'rowKey', headerName: 'Row Key', minWidth: 160, flex: 1 },
      { field: 'previousStatus', headerName: 'Previous', minWidth: 140, flex: 1 },
      { field: 'currentStatus', headerName: 'Current', minWidth: 140, flex: 1 },
    ],
    []
  );

  const actionRequiredColumns = useMemo(
    () => [
      {
        field: 'changedAt',
        headerName: 'Changed',
        minWidth: 170,
        flex: 1,
        renderCell: (params) => formatDateTime(params?.row?.changedAt ?? params?.value),
      },
      { field: 'changeType', headerName: 'Change', minWidth: 120, flex: 0.8 },
      { field: 'programName', headerName: 'Program', minWidth: 140, flex: 1 },
      { field: 'bac', headerName: 'BAC', minWidth: 110, flex: 1 },
      { field: 'dealerName', headerName: 'Dealer', minWidth: 180, flex: 1.2 },
      { field: 'previousStatus', headerName: 'Previous', minWidth: 140, flex: 1 },
      { field: 'newStatus', headerName: 'New', minWidth: 140, flex: 1 },
    ],
    []
  );

  const vmtColumns = useMemo(
    () => [
      {
        field: 'createdAt',
        headerName: 'Created',
        minWidth: 170,
        flex: 1,
        renderCell: (params) => formatDateTime(params?.row?.createdAt ?? params?.value),
      },
      { field: 'status', headerName: 'Status', minWidth: 120, flex: 1 },
      { field: 'statusCode', headerName: 'Code', minWidth: 90, flex: 0.6 },
      { field: 'recordCount', headerName: 'Records', minWidth: 110, flex: 0.8 },
      { field: 'environment', headerName: 'Env', minWidth: 100, flex: 0.6 },
      { field: 'userEmail', headerName: 'User', minWidth: 180, flex: 1 },
    ],
    []
  );

  const autoStatusColumns = useMemo(
    () => [
      {
        field: 'changedAt',
        headerName: 'Changed',
        minWidth: 170,
        flex: 1,
        renderCell: (params) => formatDateTime(params?.row?.changedAt ?? params?.value),
      },
      { field: 'bac', headerName: 'BAC', minWidth: 110, flex: 0.7 },
      { field: 'salesforceAccountName', headerName: 'Salesforce Account', minWidth: 220, flex: 1.2 },
      { field: 'orderItemId', headerName: 'Order Item ID', minWidth: 180, flex: 1 },
      { field: 'programName', headerName: 'Program', minWidth: 140, flex: 0.8 },
      { field: 'dpePreviousStatus', headerName: 'DPE Previous', minWidth: 150, flex: 0.8 },
      { field: 'dpeNewStatus', headerName: 'DPE New', minWidth: 150, flex: 0.8 },
      { field: 'diStatus', headerName: 'DI Status', minWidth: 140, flex: 0.8 },
      { field: 'diEffectiveDate', headerName: 'DI Effective', minWidth: 140, flex: 0.8 },
      { field: 'diServiceStartDate', headerName: 'DI Service Start', minWidth: 150, flex: 0.8 },
      { field: 'dpeIsBilling', headerName: 'DPE Is Billing', minWidth: 130, flex: 0.7 },
      { field: 'syncStatus', headerName: 'Sync Status', minWidth: 130, flex: 0.8 },
      { field: 'failureReason', headerName: 'Failure Reason', minWidth: 220, flex: 1.3 },
      { field: 'isManual', headerName: 'Manual', minWidth: 100, flex: 0.6 },
    ],
    []
  );

  const activeDay = days[normalizedActiveIndex] || null;
  const totalDays = days.length;
  const sectionStateByDay = prefs?.sectionsByDay || {};

  const isSectionExpanded = (key) => {
    const dayKey = activeDay?.dayKey;
    if (!dayKey) return DEFAULT_SECTION_STATE[key] ?? true;
    const dayState = sectionStateByDay[dayKey] || {};
    if (dayState[key] === undefined) return DEFAULT_SECTION_STATE[key] ?? true;
    return Boolean(dayState[key]);
  };

  const setSectionExpanded = (key, expanded) => {
    const dayKey = activeDay?.dayKey;
    if (!dayKey) return;
    const next = {
      ...prefs,
      sectionsByDay: {
        ...(prefs.sectionsByDay || {}),
        [dayKey]: {
          ...(prefs.sectionsByDay?.[dayKey] || {}),
          [key]: expanded,
        },
      },
    };
    setPrefs(next);
    savePrefs(next);
  };

  const filteredSections = useMemo(() => {
    if (!activeDay) return {};
    const raw = activeDay.sections || {};
    return {
      triggerExecutions: applyFilters(raw.triggerExecutions?.rows, filters),
      dpeStatusChanges: applyFilters(raw.dpeStatusChanges?.rows, filters),
      appsheetStatusChanges: applyFilters(raw.appsheetStatusChanges?.rows, filters),
      actionRequiredChanges: applyFilters(raw.actionRequiredChanges?.rows, filters),
      vmtPushes: applyFilters(raw.vmtPushes?.rows, filters),
      autoStatusUpdates: applyFilters(raw.autoStatusUpdates?.rows, filters),
      dbErrorLogs: applyFilters(raw.dbErrorLogs?.rows, filters),
    };
  }, [activeDay, filters]);

  const previousDay = days[normalizedActiveIndex + 1] || null;
  const previousSections = useMemo(() => {
    if (!previousDay) return null;
    const raw = previousDay.sections || {};
    return {
      dpeStatusChanges: applyFilters(raw.dpeStatusChanges?.rows, filters),
      actionRequiredChanges: applyFilters(raw.actionRequiredChanges?.rows, filters),
    };
  }, [previousDay, filters]);

  const renderContainer = (content) => (
    <PresentationMode header="Daily Digest">
      <Paper data-testid="data-story-root" variant="outlined" sx={{ p: { xs: 1.5, md: 2.5 }, borderRadius: 3 }}>
        {content}
      </Paper>
    </PresentationMode>
  );

  if (isLoading) {
    return renderContainer(
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 360 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return renderContainer(<Alert severity="error">{error?.message || 'Failed to load daily digest.'}</Alert>);
  }

  if (!activeDay) {
    return renderContainer(<Alert severity="info">No daily digest data available yet.</Alert>);
  }

  const dpeSummary = computeStatusCounts(filteredSections.dpeStatusChanges || []);
  const triggerCount = filteredSections.triggerExecutions?.length || 0;
  const dpeCount = filteredSections.dpeStatusChanges?.length || 0;
  const appsheetCount = filteredSections.appsheetStatusChanges?.length || 0;
  const actionRequiredCount = filteredSections.actionRequiredChanges?.length || 0;
  const vmtCount = filteredSections.vmtPushes?.length || 0;
  const autoStatusCount = filteredSections.autoStatusUpdates?.length || 0;
  const dbErrorCount = filteredSections.dbErrorLogs?.length || 0;

  const dpeDelta = previousSections
    ? dpeCount - (previousSections.dpeStatusChanges?.length || 0)
    : null;
  const actionRequiredDelta = previousSections
    ? actionRequiredCount - (previousSections.actionRequiredChanges?.length || 0)
    : null;

  const programs = [ALL_PROGRAMS, ...PROGRAMS];

  const buildSectionLink = (sectionKey) => {
    const params = new URLSearchParams();
    if (filters.programName && filters.programName !== ALL_PROGRAMS) params.set('programName', filters.programName);
    if (String(filters.bac || '').trim()) params.set('bac', String(filters.bac).trim());
    if (String(filters.search || '').trim()) params.set('search', String(filters.search).trim());
    if (sectionKey === 'dbErrorLogs') params.set('level', 'warning,error');
    const query = params.toString();
    return query ? `/daily-digest/section/${sectionKey}?${query}` : `/daily-digest/section/${sectionKey}`;
  };

  return renderContainer(
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <CalendarMonthIcon color="primary" />
        <Box sx={{ flex: 1, minWidth: 220 }}>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            {formatDayLabel(activeDay.dayKey)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {activeDay.dayKey} · Central Time
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <IconButton
            aria-label="Previous day"
            onClick={() => {
              if (totalDays <= 1) return;
              const nextIndex = (normalizedActiveIndex - 1 + totalDays) % totalDays;
              setActiveDayKey(days[nextIndex]?.dayKey || null);
            }}
            disabled={totalDays <= 1}
          >
            <ChevronLeftIcon />
          </IconButton>
          <Typography variant="body2" color="text.secondary">
            {totalDays > 0 ? `${normalizedActiveIndex + 1} / ${totalDays}` : '—'}
          </Typography>
          <IconButton
            aria-label="Next day"
            onClick={() => {
              if (totalDays <= 1) return;
              const nextIndex = (normalizedActiveIndex + 1) % totalDays;
              setActiveDayKey(days[nextIndex]?.dayKey || null);
            }}
            disabled={totalDays <= 1}
          >
            <ChevronRightIcon />
          </IconButton>
        </Stack>
      </Box>

      <Divider sx={{ my: 2 }} />

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Program</InputLabel>
          <Select
            value={filters.programName}
            label="Program"
            onChange={(event) => setFilters((prev) => ({ ...prev, programName: event.target.value }))}
          >
            {programs.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          size="small"
          label="BAC"
          value={filters.bac}
          onChange={(event) => setFilters((prev) => ({ ...prev, bac: event.target.value }))}
        />
        <TextField
          size="small"
          label="Search"
          value={filters.search}
          onChange={(event) => setFilters((prev) => ({ ...prev, search: event.target.value }))}
          placeholder="Search events (ex: BAC 321985)"
          sx={{ flex: 1 }}
        />
        </Stack>

        <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }}>
          <Chip label={`Triggers ${triggerCount}`} variant="outlined" />
          <Chip label={`DPE ${dpeCount}`} variant="outlined" />
          <Chip label={`AppSheet ${appsheetCount}`} variant="outlined" />
          <Chip label={`Action Required ${actionRequiredCount}`} variant="outlined" />
          <Chip label={`VMT ${vmtCount}`} variant="outlined" />
          <Chip label={`Auto Status Updates ${autoStatusCount}`} variant="outlined" />
          <Chip label={`DB Errors ${dbErrorCount}`} variant="outlined" />
          {dpeDelta !== null && (
            <Chip
              label={`DPE Δ ${dpeDelta >= 0 ? '+' : ''}${dpeDelta}`}
              color={dpeDelta === 0 ? 'default' : dpeDelta > 0 ? 'success' : 'warning'}
              variant="outlined"
            />
          )}
          {actionRequiredDelta !== null && (
            <Chip
              label={`AR Δ ${actionRequiredDelta >= 0 ? '+' : ''}${actionRequiredDelta}`}
              color={actionRequiredDelta === 0 ? 'default' : actionRequiredDelta > 0 ? 'success' : 'warning'}
              variant="outlined"
            />
          )}
        </Stack>

        {activeDay.empty ? (
          <Alert severity="info">No Changes Today 🤷</Alert>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Accordion
              expanded={isSectionExpanded('triggerExecutions')}
              onChange={(_event, expanded) => setSectionExpanded('triggerExecutions', expanded)}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <SectionHeader title="Trigger Execution" count={triggerCount} />
              </AccordionSummary>
              <AccordionDetails>
                <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }}>
                  <ExportMenu
                    rows={filteredSections.triggerExecutions || []}
                    columns={triggerColumns}
                    filename={`daily-digest-${activeDay.dayKey}-trigger-executions`}
                  />
                  <Button
                    size="small"
                    variant="text"
                    startIcon={<OpenInNewIcon />}
                    onClick={() => navigate(buildSectionLink('triggerExecutions'))}
                  >
                    Expand
                  </Button>
                </Stack>
                {triggerCount ? (
                  <DigestTable rows={filteredSections.triggerExecutions} columns={triggerColumns} />
                ) : sources?.emailAlerts?.triggerCount === 0 ? (
                  <Alert severity="warning">No email triggers are configured yet.</Alert>
                ) : (
                  <Alert severity="info">No trigger executions for this day.</Alert>
                )}
              </AccordionDetails>
            </Accordion>

            <Accordion
              expanded={isSectionExpanded('dpeStatusChanges')}
              onChange={(_event, expanded) => setSectionExpanded('dpeStatusChanges', expanded)}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <SectionHeader title="DPE Status Changes" count={dpeCount} />
              </AccordionSummary>
              <AccordionDetails>
                <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }}>
                  <ExportMenu
                    rows={filteredSections.dpeStatusChanges || []}
                    columns={dpeColumns}
                    filename={`daily-digest-${activeDay.dayKey}-dpe-status-changes`}
                  />
                  <Button
                    size="small"
                    variant="text"
                    startIcon={<OpenInNewIcon />}
                    onClick={() => navigate(buildSectionLink('dpeStatusChanges'))}
                  >
                    Expand
                  </Button>
                </Stack>
                <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }}>
                  <Chip label={`Cooling ${dpeSummary.COOLING || 0}`} variant="outlined" />
                  <Chip label={`Cancelled ${dpeSummary.CANCELLED || 0}`} variant="outlined" />
                  <Chip label={`Opted Out ${dpeSummary.OPTED_OUT || 0}`} variant="outlined" />
                  <Chip label={`Terminated ${dpeSummary.TERMINATED || 0}`} variant="outlined" />
                </Stack>
                {dpeCount ? (
                  <DigestTable rows={filteredSections.dpeStatusChanges} columns={dpeColumns} />
                ) : (
                  <Alert severity="info">No DPE status changes for this day.</Alert>
                )}
              </AccordionDetails>
            </Accordion>

            <Accordion
              expanded={isSectionExpanded('appsheetStatusChanges')}
              onChange={(_event, expanded) => setSectionExpanded('appsheetStatusChanges', expanded)}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <SectionHeader title="AppSheet Status Changes" count={appsheetCount} />
              </AccordionSummary>
              <AccordionDetails>
                <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }}>
                  <ExportMenu
                    rows={filteredSections.appsheetStatusChanges || []}
                    columns={appsheetColumns}
                    filename={`daily-digest-${activeDay.dayKey}-appsheet-status-changes`}
                  />
                  <Button
                    size="small"
                    variant="text"
                    startIcon={<OpenInNewIcon />}
                    onClick={() => navigate(buildSectionLink('appsheetStatusChanges'))}
                  >
                    Expand
                  </Button>
                </Stack>
                {appsheetCount ? (
                  <DigestTable rows={filteredSections.appsheetStatusChanges} columns={appsheetColumns} />
                ) : sources?.appsheetStatusTracking && !sources.appsheetStatusTracking.configured ? (
                  <Alert severity="warning">AppSheet status tracking is not configured.</Alert>
                ) : (
                  <Alert severity="info">No AppSheet status changes for this day.</Alert>
                )}
              </AccordionDetails>
            </Accordion>

            <Accordion
              expanded={isSectionExpanded('actionRequiredChanges')}
              onChange={(_event, expanded) => setSectionExpanded('actionRequiredChanges', expanded)}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <SectionHeader title="Action Required Changes" count={actionRequiredCount} />
              </AccordionSummary>
              <AccordionDetails>
                <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }}>
                  <ExportMenu
                    rows={filteredSections.actionRequiredChanges || []}
                    columns={actionRequiredColumns}
                    filename={`daily-digest-${activeDay.dayKey}-action-required-changes`}
                  />
                  <Button
                    size="small"
                    variant="text"
                    startIcon={<OpenInNewIcon />}
                    onClick={() => navigate(buildSectionLink('actionRequiredChanges'))}
                  >
                    Expand
                  </Button>
                </Stack>
                {actionRequiredCount ? (
                  <DigestTable rows={filteredSections.actionRequiredChanges} columns={actionRequiredColumns} />
                ) : (
                  <Alert severity="info">No Action Required changes for this day.</Alert>
                )}
              </AccordionDetails>
            </Accordion>

            <Accordion
              expanded={isSectionExpanded('vmtPushes')}
              onChange={(_event, expanded) => setSectionExpanded('vmtPushes', expanded)}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <SectionHeader title="VMT Pushes" count={vmtCount} />
              </AccordionSummary>
              <AccordionDetails>
                <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }}>
                  <ExportMenu
                    rows={filteredSections.vmtPushes || []}
                    columns={vmtColumns}
                    filename={`daily-digest-${activeDay.dayKey}-vmt-pushes`}
                  />
                  <Button
                    size="small"
                    variant="text"
                    startIcon={<OpenInNewIcon />}
                    onClick={() => navigate(buildSectionLink('vmtPushes'))}
                  >
                    Expand
                  </Button>
                </Stack>
                {vmtCount ? (
                  <DigestTable rows={filteredSections.vmtPushes} columns={vmtColumns} />
                ) : (
                  <Alert severity="info">No VMT pushes for this day.</Alert>
                )}
              </AccordionDetails>
            </Accordion>

            <Accordion
              expanded={isSectionExpanded('autoStatusUpdates')}
              onChange={(_event, expanded) => setSectionExpanded('autoStatusUpdates', expanded)}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <SectionHeader title="Auto Status Updates" count={autoStatusCount} />
              </AccordionSummary>
              <AccordionDetails>
                <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }}>
                  <ExportMenu
                    rows={filteredSections.autoStatusUpdates || []}
                    columns={autoStatusColumns}
                    filename={`daily-digest-${activeDay.dayKey}-auto-status-updates`}
                  />
                  <Button
                    size="small"
                    variant="text"
                    startIcon={<OpenInNewIcon />}
                    onClick={() => navigate(buildSectionLink('autoStatusUpdates'))}
                  >
                    Expand
                  </Button>
                </Stack>
                {autoStatusCount ? (
                  <DigestTable rows={filteredSections.autoStatusUpdates} columns={autoStatusColumns} />
                ) : (
                  <Alert severity="info">No auto status updates for this day.</Alert>
                )}
              </AccordionDetails>
            </Accordion>

            <Accordion
              expanded={isSectionExpanded('dbErrorLogs')}
              onChange={(_event, expanded) => setSectionExpanded('dbErrorLogs', expanded)}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <SectionHeader title="DB Error logs" count={dbErrorCount} />
              </AccordionSummary>
              <AccordionDetails>
                <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }}>
                  <Button
                    size="small"
                    variant="text"
                    startIcon={<OpenInNewIcon />}
                    onClick={() => navigate(buildSectionLink('dbErrorLogs'))}
                  >
                    Expand
                  </Button>
                </Stack>
                {dbErrorCount ? (
                  <Alert severity="info">DB warning/error log details are available in the expanded view.</Alert>
                ) : (
                  <Alert severity="info">No warning/error DB logs for this day.</Alert>
                )}
              </AccordionDetails>
            </Accordion>
          </Box>
        )}
    </>
  );
};

export default DataStory;
